import type { Expert, ExpertsSearchResponse, RankBy } from "../types";

export interface RankingFactor {
  name: string;
  /** Its term in a paper's weight, for the factors that multiply it. */
  term?: string;
  text: string;
  /** A checkbox on the page, rather than always applied. */
  option: boolean;
  on: boolean;
}

/** "2016–2025, 4 since 2023": the person's years on the topic. */
export const yearsOnTopic = (e: Expert, recentSince?: number | null): string | null => {
  if (!e.first_year || !e.last_year) return null;
  const span = e.first_year === e.last_year ? `${e.first_year}` : `${e.first_year}–${e.last_year}`;
  return recentSince ? `${span}, ${e.recent_papers ?? 0} since ${recentSince}` : span;
};

/** "cited by 7 on-topic papers read": citations from within this search, not overall. */
export const citedByText = (n: number): string =>
  `cited by ${n.toLocaleString("en-US")} on-topic paper${n === 1 ? "" : "s"} read`;

/** Where a paper's citation count comes from, for its tooltip. */
export const CITATIONS_SOURCE =
  "Papers citing it in GXL's citation graph (OpenAlex when the graph does not answer in time). " +
  "A preprint and its published version are counted separately.";

/** The page's options that change a ranking. */
export interface RankingOptions {
  foundational: boolean;
  first_authors: boolean;
  recent: boolean;
  rank_by: RankBy;
}

/** The options a search ran with. */
export const optionsOf = (d: ExpertsSearchResponse): RankingOptions => ({
  foundational: d.foundational ?? true,
  first_authors: Boolean(d.first_authors),
  recent: Boolean(d.recent),
  rank_by: d.rank_by ?? "score",
});

/** The "Rank by" choices, GXL's score first. */
export const RANK_BY_LABELS: Record<RankBy, string> = {
  score: "GXL score",
  papers: "On-topic papers",
  citations: "Citations",
  h_index: "h-index",
};

/** "on-topic papers they are first or sole author of, from 2023 on": what a count counts. */
export const countedPapers = (o: RankingOptions, since: number): string =>
  "on-topic papers" +
  (o.first_authors ? " they are first or sole author of" : ", any author position") +
  (o.recent ? `, from ${since} on` : "") +
  (o.foundational ? " (foundational papers included)" : "");

/** "Rank = number of on-topic papers …": how a count ranks people; null for the score. */
export const countFormula = (o: RankingOptions, since: number): string | null => {
  const papers = countedPapers(o, since);
  switch (o.rank_by) {
    case "papers":
      return `Rank = number of ${papers}; ties go to their citations`;
    case "citations":
      return `Rank = total citations of ${papers}; ties go to the number of papers`;
    case "h_index":
      return `Rank = h-index of ${papers} (h papers cited at least h times each); ties go to their citations`;
    default:
      return null;
  }
};

/** What the person ranked by, for the number beside their name: "4.2", "12 papers", "h-index 7". */
export const rankValueText = (e: Expert, rankBy: RankBy): string => {
  const n = e.rank_value ?? e.score;
  switch (rankBy) {
    case "papers":
      return `${n} ${n === 1 ? "paper" : "papers"}`;
    case "citations":
      return `${n.toLocaleString("en-US")} citations`;
    case "h_index":
      return `h-index ${n}`;
    default:
      return e.score.toFixed(1);
  }
};

const OPTION_NAMES: [keyof RankingOptions, string][] = [
  ["foundational", "Foundational papers"],
  ["first_authors", "First authors only"],
  ["recent", "Recent work"],
];

/** ["First authors only on", …]: options checked differently from the search shown. */
export const changedOptions = (d: ExpertsSearchResponse, o: RankingOptions): string[] => {
  const ran = optionsOf(d);
  const changed = OPTION_NAMES.filter(([k]) => ran[k] !== o[k]).map(([k, name]) => `${name} ${o[k] ? "on" : "off"}`);
  return ran.rank_by === o.rank_by ? changed : [...changed, `rank by ${RANK_BY_LABELS[o.rank_by]}`];
};

/** First authors only + recent work: the people doing the work now. */
export const isEmerging = (o: { first_authors?: boolean | null; recent?: boolean | null }): boolean =>
  Boolean(o.first_authors && o.recent);

/** What a person earns of a paper's weight, by author position. */
export const shareFormula = (o: RankingOptions): string =>
  o.first_authors
    ? "1 for first and sole authors, 0 for the rest"
    : "1 for first, last and sole authors; 0.5 ÷ √m for each of m middle authors";

/** How a person's score adds up. */
export const scoreFormula = (o: RankingOptions): string =>
  `sum of paper weight × authorship credit over ${o.foundational ? "the papers read and the foundational papers" : "the papers read"}`;

/** What went into a ranking, in the order it applies; ``o`` (the options checked, by default
 * the ones the search ran with) says which options apply. */
export const rankingFactors = (d: ExpertsSearchResponse, o: RankingOptions = optionsOf(d)): RankingFactor[] => {
  const c = d.counts;
  const since = d.recent_since ?? new Date().getFullYear() - 3;
  return [
    {
      name: "P(on-topic)",
      term: "P(on-topic)",
      text:
        "The probability, from GXL's model reading each title and abstract, that the topic" +
        (d.topic_guide ? ", as read above," : "") +
        " is a main subject of the paper, multiplied by 1 − P(out-of-scope topic) for each " +
        "out-of-scope topic the definition lists (broader fields excepted). " +
        `Papers below ${d.min_relevance ?? 0.5} get weight 0 ` +
        `(${c.relevant.toLocaleString("en-US")} of ${c.candidates.toLocaleString("en-US")} passed).`,
      option: false,
      on: true,
    },
    {
      name: "Review factor",
      term: "(1 − 0.7 × P(review))",
      text:
        "0.3 for a review, 1 for primary research. P(review) is the model's probability that the " +
        "paper reviews other studies rather than reporting its own data or analysis. Papers " +
        "without an abstract are not classified as reviews.",
      option: false,
      on: true,
    },
    {
      name: "Log citations",
      term: "(1 + ln(1 + citations))²",
      text:
        "Citations: papers citing it in GXL's citation graph (OpenAlex when the graph does not " +
        "answer in time); a preprint and its published version are counted separately. " +
        "1 at 0 citations, 11.5 at 10, 31.5 at 100, 62.5 at 1,000.",
      option: false,
      on: true,
    },
    {
      name: "Recency decay (Recent work)",
      term: "0.5^(years since publication ÷ 3)",
      text:
        `A half-life of 3 years: a ${since} paper gets 0.5, a ${since - 3} paper 0.25. ` +
        "Favors researchers active now.",
      option: true,
      on: o.recent,
    },
    {
      name: "Authorship credit",
      text: o.first_authors
        ? "1 for first and sole authors; 0 for middle and last authors."
        : "1 for first, last and sole authors; each of m middle authors receives 0.5 ÷ √m " +
          "(0.25 each when m = 4).",
      option: false,
      on: true,
    },
    {
      name: "First authors only",
      text:
        "Only first and sole authors, typically the researchers who carried out the work, " +
        "receive credit; senior (last) authors receive none.",
      option: true,
      on: o.first_authors,
    },
    {
      name: "Foundational papers",
      text:
        "Keyword search under-retrieves older landmark papers. The 150 papers most cited by the " +
        "on-topic papers (each cited by at least 3 of them, arXiv preprints included) are added " +
        "and scored like the rest.",
      option: true,
      on: o.foundational,
    },
  ];
};

/** "P(on-topic) × … × (1 + ln(1 + citations))²": the terms that multiply a paper's weight. */
export const weightFormula = (factors: RankingFactor[]): string =>
  factors
    .filter((f) => f.on && f.term)
    .map((f) => f.term)
    .join(" × ");
